import React, { useState, useEffect } from 'react';
import { X, Truck, User, Calendar, MapPin, Clock, FileText, Save, Navigation } from 'lucide-react';
import '../styles/ConsignmentEntryFormLT.css';
import apiClient from '../services/api';

const VehicleAssignmentsForm = ({ onClose, onSave, editData = null }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;

  const [formData, setFormData] = useState({
    VehicleID: '',
    DriverID: '',
    AssignmentDate: new Date().toISOString().slice(0, 10),
    DepartureTime: '',
    ArrivalTime: '',
    Origin: '',
    Destination: '',
    Route: '',
    Purpose: '',
    Odometer_Start: '',
    Odometer_End: '',
    Status: 'Assigned',
    Remarks: ''
  });

  useEffect(() => {
    loadDropdownData();
    
    if (editData && editData.AssignmentID) {
      setFormData({
        ...editData,
        AssignmentDate: editData.AssignmentDate || new Date().toISOString().slice(0, 10),
        DepartureTime: editData.DepartureTime || '',
        ArrivalTime: editData.ArrivalTime || '',
        Origin: editData.Origin || '',
        Destination: editData.Destination || '',
        Route: editData.Route || '',
        Purpose: editData.Purpose || '',
        Odometer_Start: editData.Odometer_Start || '',
        Odometer_End: editData.Odometer_End || '',
        Status: editData.Status || 'Assigned',
        Remarks: editData.Remarks || ''
      });
    }
  }, []);

  const loadDropdownData = async () => {
    try {
      const [vehicleRes, driverRes] = await Promise.all([
        apiClient.get('/Vehicles.php').catch(e => ({ data: { success: false } })),
        apiClient.get('/Drivers.php').catch(e => ({ data: { success: false } }))
      ]);

      if (vehicleRes.data.success) {
        const allVehicles = vehicleRes.data.data || [];
        const activeVehicles = allVehicles.filter(v => v.IsActive || v.isactive);
        setVehicles(activeVehicles);
      } else {
        setVehicles([]);
      }

      if (driverRes.data.success) {
        const allDrivers = driverRes.data.data || [];
        const activeDrivers = allDrivers.filter(d => d.isactive === 1);
        setDrivers(activeDrivers);
      } else {
        setDrivers([]);
      }
    } catch (err) {
      console.error('Error loading dropdown data:', err);
      setError('Failed to load dropdown data');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.VehicleID) {
      setError('Vehicle is required');
      return;
    }
    
    if (!formData.DriverID) {
      setError('Driver is required');
      return;
    }
    
    if (!formData.AssignmentDate) {
      setError('Assignment date is required');
      return;
    }
    
    if (!formData.Origin.trim()) {
      setError('Origin is required');
      return;
    }
    
    if (!formData.Destination.trim()) {
      setError('Destination is required');
      return;
    }
    
    setLoading(true);
    setError('');

    try {
      const dataToSave = {
        ...formData,
        VehicleID: formData.VehicleID || null,
        DriverID: formData.DriverID || null,
        Odometer_Start: formData.Odometer_Start || null,
        Odometer_End: formData.Odometer_End || null
      };
      
      // Include AssignmentID if editing
      if (editData && editData.AssignmentID) {
        dataToSave.AssignmentID = editData.AssignmentID;
      }
      
      await onSave(dataToSave);
    } catch (err) {
      setError(err.message || 'Failed to save assignment');
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!companyId) {
    return (
      <div className="consignment-form-overlay">
        <div className="consignment-form-container" style={{ maxWidth: '600px' }}>
          <div className="form-header">
            <h2>Session Required</h2>
            <button className="close-btn" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', textAlign: 'center' }}>
            <p>Please select a company and financial year from the dashboard.</p>
            <button onClick={onClose} className="btn btn-primary" style={{ marginTop: '16px' }}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="consignment-form-overlay">
      <div className="consignment-form-container" style={{ maxWidth: '1000px' }}>
        {/* ✅ GRADIENT HEADER - Same style as Consignment forms */}
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          padding: '28px 32px',
          borderRadius: '12px 12px 0 0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '4px solid #d97706',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flex: 1 }}>
            <div style={{
              width: '60px',
              height: '60px',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}>
              <Truck size={24} color="#ffffff" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <div style={{ 
                  color: '#ffffff',
                  fontSize: '1.8rem',
                  fontWeight: '700',
                  textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                }}>
                  {editData ? 'Edit' : 'New'} Vehicle Assignment
                </div>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              color: '#ffffff',
              width: '44px',
              height: '44px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.35)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
            }}
          >
            <X size={20} color="#ffffff" />
          </button>
        </div>

        {error && (
          <div className="alert alert-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-content">
            
            {/* Vehicle & Driver Selection */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Truck size={18} />
                Vehicle & Driver Selection
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Vehicle *</label>
                  <select
                    name="VehicleID"
                    value={formData.VehicleID}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Vehicle --</option>
                    {vehicles.map(vehicle => (
                      <option key={vehicle.VehicleID} value={vehicle.VehicleID}>
                        {vehicle.RegistrationNo} - {vehicle.VehicleType}
                      </option>
                    ))}
                  </select>
                  {vehicles.length === 0 && (
                    <small style={{ color: '#ef4444', fontSize: '12px' }}>
                      No active vehicles available
                    </small>
                  )}
                </div>
                <div className="form-group">
                  <label>Driver *</label>
                  <select
                    name="DriverID"
                    value={formData.DriverID}
                    onChange={handleChange}
                    required
                  >
                    <option value="">-- Select Driver --</option>
                    {drivers.map(driver => (
                      <option key={driver.driverid} value={driver.driverid}>
                        {driver.drivername}
                      </option>
                    ))}
                  </select>
                  {drivers.length === 0 && (
                    <small style={{ color: '#ef4444', fontSize: '12px' }}>
                      No active drivers available
                    </small>
                  )}
                </div>
              </div>
            </div>

            {/* Assignment Date & Status */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Calendar size={18} />
                Assignment Details
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Assignment Date *</label>
                  <input
                    type="date"
                    name="AssignmentDate"
                    value={formData.AssignmentDate}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select
                    name="Status"
                    value={formData.Status}
                    onChange={handleChange}
                  >
                    <option value="Assigned">Assigned</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Route Information */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title" style={{ background: '#fef3c7', padding: '8px 12px', borderRadius: '6px' }}>
                <MapPin size={18} />
                Route Information
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Origin (Starting Location) *</label>
                  <input
                    type="text"
                    name="Origin"
                    value={formData.Origin}
                    onChange={handleChange}
                    placeholder="e.g., Dubai Warehouse"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Destination (Ending Location) *</label>
                  <input
                    type="text"
                    name="Destination"
                    value={formData.Destination}
                    onChange={handleChange}
                    placeholder="e.g., Abu Dhabi Port"
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label>Route (Via Locations)</label>
                  <input
                    type="text"
                    name="Route"
                    value={formData.Route}
                    onChange={handleChange}
                    placeholder="e.g., Dubai - Sharjah - Ajman - Abu Dhabi"
                  />
                </div>
              </div>
            </div>

            {/* Time & Odometer */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <Clock size={18} />
                Time & Distance
              </h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Departure Time</label>
                  <input
                    type="time"
                    name="DepartureTime"
                    value={formData.DepartureTime}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Arrival Time</label>
                  <input
                    type="time"
                    name="ArrivalTime"
                    value={formData.ArrivalTime}
                    onChange={handleChange}
                  />
                </div>
                <div className="form-group">
                  <label>Odometer Start (KM)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="Odometer_Start"
                    value={formData.Odometer_Start}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label>Odometer End (KM)</label>
                  <input
                    type="number"
                    step="0.01"
                    name="Odometer_End"
                    value={formData.Odometer_End}
                    onChange={handleChange}
                    placeholder="0.00"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* Purpose & Remarks */}
            <div className="form-section" style={{ marginBottom: '16px' }}>
              <h3 className="section-title">
                <FileText size={18} />
                Additional Information
              </h3>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Purpose of Trip</label>
                  <input
                    type="text"
                    name="Purpose"
                    value={formData.Purpose}
                    onChange={handleChange}
                    placeholder="e.g., Delivery to client, Pick up shipment, Maintenance"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Remarks</label>
                  <textarea
                    name="Remarks"
                    value={formData.Remarks}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Any additional notes or special instructions..."
                  />
                </div>
              </div>
            </div>

          </div>

          <div className="form-footer">
            <button
              type="button"
              className="btn btn-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            
            <button
              type="submit"
              className="btn btn-save"
              disabled={loading}
              style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}
            >
              {loading ? (
                <>
                  <div className="btn-spinner" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={18} />
                  {editData ? 'Update' : 'Save'} Assignment
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleAssignmentsForm;